/**
 * Bank transfer instructions page — shown after order creation when payment_method=virement.
 * URL: /virement/:orderId
 * Customer can also upload a proof of payment (optional).
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useLang, useSettings, fmtDzd, assetUrl } from "../lib/providers";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Copy, Upload, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";

export default function VirementPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success(lang === "ar" ? "تم النسخ" : "Copié !");
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd);
      await axios.post(`${API}/orders/${orderId}/payment-proof`, { proof_url: data.url });
      setOrder((o) => ({ ...o, payment_proof_url: data.url }));
      toast.success(lang === "ar" ? "تم رفع الإثبات" : "Preuve envoyée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white">…</div>;
  if (!order) return (
    <div className="min-h-screen text-white">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-16 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto text-clay mb-4" />
        <h1 className="font-heading font-black text-2xl">Commande introuvable</h1>
        <Button onClick={() => navigate("/")} className="mt-6 bg-neon text-black rounded-none uppercase tracking-widest">Retour</Button>
      </main>
      <Footer />
    </div>
  );

  const ref = order.id;
  const deadline = order.payment_deadline ? new Date(order.payment_deadline) : null;
  const expired = deadline && deadline < new Date();
  const paid = order.payment_status === "paid";
  const ribLines = (settings.rib_info || "").split("\n").filter(Boolean);
  const waPhone = (settings.whatsapp_dz || "").replace(/\D/g, "");
  const waLink = waPhone
    ? `https://wa.me/${waPhone}?text=${encodeURIComponent(`Salam, Bonjour, j'ai effectué le virement pour la commande ${ref}.`)}`
    : null;

  return (
    <div className="min-h-screen text-white bg-court" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="virement-page">
        {paid ? (
          <div className="border border-neon/40 bg-neon/5 p-6 mb-6 text-center" data-testid="virement-paid">
            <CheckCircle2 className="w-12 h-12 mx-auto text-neon mb-3" />
            <h1 className="font-heading font-black uppercase text-2xl">{lang === "ar" ? "تم استلام دفعتك ✅" : "Paiement reçu ✅"}</h1>
            <p className="text-white/70 mt-2">{lang === "ar" ? "نقوم بتحضير طلبك." : "Nous préparons votre commande."}</p>
          </div>
        ) : expired ? (
          <div className="border border-clay/40 bg-clay/5 p-6 mb-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-clay mb-3" />
            <h1 className="font-heading font-black uppercase text-2xl">{lang === "ar" ? "انتهت مهلة الدفع" : "Délai de paiement expiré"}</h1>
            <p className="text-white/70 mt-2">{lang === "ar" ? "تواصل معنا عبر واتساب لإعادة تفعيل الطلب." : "Contactez-nous via WhatsApp pour réactiver la commande."}</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl mb-2">
              {lang === "ar" ? "إرشادات التحويل المصرفي" : "Instructions de virement"}
            </h1>
            <p className="text-white/60 text-sm mb-6">
              {lang === "ar" ? (settings.bank_transfer_instructions_ar || "") : (settings.bank_transfer_instructions_fr || "")}
            </p>

            {/* Order summary */}
            <div className="border border-white/10 bg-black/30 p-5 mb-5 space-y-2 text-sm" data-testid="virement-summary">
              <Row label={lang === "ar" ? "رقم الطلب" : "N° de commande"} value={ref} onCopy={() => copy(ref)} testid="row-ref" />
              <Row label={lang === "ar" ? "المبلغ بالضبط" : "Montant exact"} value={fmtDzd(order.total_dzd)} onCopy={() => copy(String(order.total_dzd))} testid="row-amount" />
              {deadline && (
                <Row label={lang === "ar" ? "آخر أجل" : "Délai maximum"} value={deadline.toLocaleString("fr-FR", { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })} testid="row-deadline" icon={<Clock className="w-4 h-4 text-clay" />} />
              )}
            </div>

            {/* Bank details */}
            <div className="border border-neon/40 bg-neon/5 p-5 mb-5" data-testid="virement-bank">
              <p className="text-[10px] uppercase tracking-widest text-neon font-bold mb-3">{lang === "ar" ? "تفاصيل الحساب" : "Coordonnées bancaires"}</p>
              {settings.bank_transfer_beneficiary && (
                <Row label={lang === "ar" ? "المستفيد" : "Bénéficiaire"} value={settings.bank_transfer_beneficiary} onCopy={() => copy(settings.bank_transfer_beneficiary)} />
              )}
              {ribLines.map((line, i) => (
                <Row key={i} label="" value={line} onCopy={() => copy(line)} />
              ))}
            </div>

            {/* Proof upload */}
            <div className="border border-white/10 p-5 mb-5">
              <p className="font-bold text-sm mb-2">📎 {lang === "ar" ? "إرسال إثبات التحويل (اختياري)" : "Envoyer une preuve de virement (optionnel)"}</p>
              <p className="text-xs text-white/50 mb-3">
                {lang === "ar" ? "صورة من إيصال البنك تساعدنا على معالجة طلبك بسرعة." : "Une photo du reçu bancaire nous aide à traiter votre commande plus vite."}
              </p>
              {order.payment_proof_url ? (
                <div className="flex items-center gap-3" data-testid="proof-uploaded">
                  <CheckCircle2 className="w-5 h-5 text-neon" />
                  <a href={assetUrl(order.payment_proof_url)} target="_blank" rel="noreferrer" className="text-xs text-neon underline">
                    {lang === "ar" ? "تم إرسال الإثبات" : "Preuve envoyée — voir"}
                  </a>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 cursor-pointer bg-neon/10 hover:bg-neon/20 border border-neon/40 px-4 py-2 text-xs uppercase tracking-widest font-bold">
                  <Upload className="w-4 h-4" />
                  <span>{uploading ? "…" : (lang === "ar" ? "اختر صورة" : "Choisir une photo")}</span>
                  <input type="file" accept="image/*,application/pdf" onChange={handleUpload} className="hidden" data-testid="proof-input" />
                </label>
              )}
            </div>

            {waLink && (
              <a href={waLink} target="_blank" rel="noreferrer" className="block w-full text-center bg-[#25D366] hover:opacity-90 text-white font-black uppercase tracking-widest py-4" data-testid="virement-wa">
                {lang === "ar" ? "إرسال الإثبات عبر واتساب" : "Envoyer la preuve par WhatsApp"}
              </a>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Row({ label, value, onCopy, testid, icon }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-white/60">{icon}{label && <span>{label}</span>}</div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm">{value}</span>
        {onCopy && <button onClick={onCopy} className="text-white/40 hover:text-neon"><Copy className="w-3.5 h-3.5" /></button>}
      </div>
    </div>
  );
}
